import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShoppingCart, Clock, BarChart2, User as UserIcon, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import api from "@/lib/api";
import { CATEGORY_LABELS, CATEGORY_IMAGES, formatPrice } from "@/lib/constants";
import { useCurrency } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currency } = useCurrency();
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <>
        <AppHeader title="Programa" showBack showLogo={false} />
        <div className="p-4 animate-pulse">
          <div className="w-full h-56 bg-slate-100 rounded-2xl" />
          <div className="h-6 bg-slate-100 rounded w-3/4 mt-4" />
          <div className="h-4 bg-slate-100 rounded w-1/2 mt-3" />
        </div>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <AppHeader title="Programa" showBack showLogo={false} />
        <div className="p-6 text-center text-slate-500">Producto no encontrado.</div>
      </>
    );
  }

  const handleAdd = () => {
    addItem(product, 1);
    toast.success("Agregado al carrito", { description: product.title });
  };

  return (
    <>
      <AppHeader title="Programa" showBack showLogo={false} />
      <main className="page-scroll" data-testid="product-detail-page">
        <div className="px-4 pt-3">
          <img
            src={product.images?.[0] || CATEGORY_IMAGES[product.category]}
            alt={product.title}
            data-testid="product-image"
            className="w-full h-60 object-cover rounded-2xl"
          />
        </div>
        <div className="px-4 mt-4">
          <span className="badge-cat">{CATEGORY_LABELS[product.category]}</span>
          <h1 data-testid="product-title" className="font-heading text-2xl font-extrabold text-[#0A2A66] mt-2 leading-tight">
            {product.title}
          </h1>
          <p className="text-slate-600 text-sm mt-2">{product.short_description}</p>

          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="card-clean py-2.5">
              <Clock size={16} className="mx-auto text-[#1E5FD9]" />
              <div className="text-[11px] text-slate-500 mt-1">Duración</div>
              <div className="text-xs font-semibold text-[#0A2A66]">{product.duration || "—"}</div>
            </div>
            <div className="card-clean py-2.5">
              <BarChart2 size={16} className="mx-auto text-[#1E5FD9]" />
              <div className="text-[11px] text-slate-500 mt-1">Nivel</div>
              <div className="text-xs font-semibold text-[#0A2A66]">{product.level || "—"}</div>
            </div>
            <div className="card-clean py-2.5">
              <UserIcon size={16} className="mx-auto text-[#1E5FD9]" />
              <div className="text-[11px] text-slate-500 mt-1">Docente</div>
              <div className="text-xs font-semibold text-[#0A2A66] truncate">{product.instructor || "NEO"}</div>
            </div>
          </div>

          <div className="mt-5">
            <h3 className="font-heading text-base font-bold text-[#0A2A66]">Descripción</h3>
            <p className="text-sm text-slate-600 leading-relaxed mt-2 whitespace-pre-line">{product.description}</p>
          </div>

          <div className="mt-5">
            <h3 className="font-heading text-base font-bold text-[#0A2A66]">Incluye</h3>
            <ul className="mt-2 space-y-2">
              {["Acceso 24/7 a la plataforma", "Certificado digital al finalizar", "Comunidad activa y soporte docente"].map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle size={16} className="text-[#1E5FD9] mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="h-40" />
      </main>

      {/* Sticky purchase bar - positioned above bottom-nav */}
      <div className="fixed left-0 right-0 z-50 bg-white border-t border-slate-200 px-4 py-3" style={{ bottom: "68px", paddingBottom: "0.5rem" }}>
        <div className="mx-auto max-w-[480px] flex items-center gap-3">
          <div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wider">Precio</div>
            <div className="font-heading text-lg font-extrabold text-[#0A2A66]" data-testid="product-price">
              {formatPrice(product.price_usd, currency)}
            </div>
          </div>
          <button
            onClick={handleAdd}
            data-testid="add-to-cart-btn"
            className="gold-btn flex-1 flex items-center justify-center gap-2 py-3.5"
          >
            <ShoppingCart size={16} /> Agregar al carrito
          </button>
        </div>
      </div>
    </>
  );
}
